import { useCallback, useEffect, useState } from 'react'
import {
  getVapidPublicKey,
  subscribePush,
  testPush,
  unsubscribePush,
  type PushTestResponse,
} from '../lib/api.js'
import {
  ensureSwRegistration,
  getCurrentSubscription,
  isPushSupported,
  permissionStatus,
  urlBase64ToUint8Array,
} from '../lib/push.js'

export interface UseWebPushResult {
  isSupported: boolean
  permission: NotificationPermission | 'unsupported'
  isSubscribed: boolean
  isLoading: boolean
  error: string | null
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
  sendTest: () => Promise<PushTestResponse>
  refreshState: () => Promise<void>
  clearError: () => void
}

function extractKeys(sub: PushSubscription): { p256dh: string; auth: string } | null {
  const json = sub.toJSON()
  const keys = json.keys
  if (!keys || !keys['p256dh'] || !keys['auth']) return null
  return { p256dh: keys['p256dh'], auth: keys['auth'] }
}

export function useWebPush(): UseWebPushResult {
  const [isSupported] = useState<boolean>(() => isPushSupported())
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    permissionStatus(),
  )
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const refreshState = useCallback(async (): Promise<void> => {
    setPermission(permissionStatus())
    if (!isPushSupported()) {
      setIsSubscribed(false)
      return
    }
    try {
      const current = await getCurrentSubscription()
      setIsSubscribed(current !== null)
    } catch {
      setIsSubscribed(false)
    }
  }, [])

  useEffect(() => {
    void refreshState()
  }, [refreshState])

  const subscribe = useCallback(async (): Promise<void> => {
    if (!isPushSupported()) {
      setError('Notifications push non supportées par ce navigateur')
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      let perm = Notification.permission
      if (perm === 'default') {
        perm = await Notification.requestPermission()
      }
      setPermission(perm)
      if (perm !== 'granted') {
        throw new Error('Permission refusée pour les notifications')
      }

      const registration = await ensureSwRegistration()
      const vapidKey = await getVapidPublicKey()

      const existing = await registration.pushManager.getSubscription()
      // Cast: lib.dom v5+ types BufferSource as ArrayBufferView<ArrayBuffer>, but
      // Uint8Array is Uint8Array<ArrayBufferLike> in newer @types. Functionally identical.
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as BufferSource,
        }))

      const keys = extractKeys(subscription)
      if (!keys) {
        throw new Error('Clés de chiffrement manquantes sur la souscription')
      }

      await subscribePush({
        endpoint: subscription.endpoint,
        keys,
        userAgent: navigator.userAgent,
      })

      setIsSubscribed(true)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur inconnue'
      setError(`Activation impossible : ${msg}`)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!isPushSupported()) return
    setIsLoading(true)
    setError(null)
    try {
      const subscription = await getCurrentSubscription()
      if (!subscription) {
        setIsSubscribed(false)
        return
      }
      const endpoint = subscription.endpoint
      await subscription.unsubscribe()
      try {
        await unsubscribePush(endpoint)
      } catch {
        // Backend cleanup is best-effort: the local subscription is already gone,
        // and the next push attempt will prune the stale row.
      }
      setIsSubscribed(false)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur inconnue'
      setError(`Désactivation impossible : ${msg}`)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const sendTest = useCallback(async (): Promise<PushTestResponse> => {
    setIsLoading(true)
    setError(null)
    try {
      return await testPush()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur inconnue'
      setError(`Envoi du test impossible : ${msg}`)
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    sendTest,
    refreshState,
    clearError,
  }
}
